<script lang="ts">
  import TopBar from '../components/TopBar.svelte';
  import PixelAvatar from '../components/PixelAvatar.svelte';
  import PixelButton from '../components/PixelButton.svelte';
  import PixelDialog from '../components/PixelDialog.svelte';
  import PixelIcon from '../components/PixelIcon.svelte';
  import PixelSlider from '../components/PixelSlider.svelte';
  import PixelSwitch from '../components/PixelSwitch.svelte';
  import { i18n } from '../i18n/index.svelte';
  import { auth } from '../stores/auth.svelte';
  import { LANGUAGES, type Language } from '../stores/displaySettings';
  import { router } from '../stores/router.svelte';
  import { session } from '../stores/session.svelte';
  import { settings } from '../stores/settings.svelte';
  import { toasts } from '../stores/toasts.svelte';

  /**
   * Preferences. Every control here writes through to the system that actually
   * owns the value — the audio buses, the haptics manager, the motion policy,
   * the catalogue — so nothing on this screen is a copy that can disagree with
   * what is in effect.
   *
   * Changes are heard and seen at once but saved on request. Volume, CRT and
   * language are preferences nobody can judge from a label: they have to be
   * tried. Committing each attempt as it happened meant a player who slid a bar
   * to hear it had already changed the setting, and the screen answered with a
   * toast per movement. So the screen previews live and keeps one explicit
   * commit — which is also the only reason it can offer to put everything back.
   */

  const t = $derived(i18n.t);
  const soundOn = $derived(!settings.audio.master.muted);

  /** Open while the player is being asked what to do about unsaved changes. */
  let exitPrompt = $state(false);

  $effect(() => {
    settings.beginEdit();
    return () => settings.endEdit();
  });

  /**
   * The exit guard lives in the router, not in this screen's buttons: the
   * browser's back arrow, Android's hardware key and a hand-typed URL all leave
   * this view without touching anything in it, and those are precisely the ways
   * an unsaved change would otherwise be lost without a word.
   */
  $effect(() =>
    router.guardNavigation(() => {
      if (!settings.dirty) return true;
      exitPrompt = true;
      return false;
    }),
  );

  function save(): void {
    settings.save();
    toasts.success(i18n.t.toasts.settingsSaved, { key: 'settings:saved' });
  }

  function saveAndExit(): void {
    save();
    exitPrompt = false;
    router.resumePending();
  }

  function discardAndExit(): void {
    settings.discard();
    exitPrompt = false;
    router.resumePending();
  }

  function keepEditing(): void {
    exitPrompt = false;
    router.cancelPending();
  }

  /**
   * Step through the available languages.
   *
   * Both arrows are live and, with two languages, both land on the same place —
   * which is the honest behaviour rather than a special case: the control means
   * "the next one along", and it keeps working the day a third is added.
   */
  function stepLanguage(direction: 1 | -1): void {
    const index = LANGUAGES.indexOf(settings.display.language);
    const next = LANGUAGES[(index + direction + LANGUAGES.length) % LANGUAGES.length] as Language;
    settings.setLanguage(next);
  }

  let signingOut = $state(false);

  /**
   * Signing out is a real action rather than a disabled button: there is
   * something to sign back in with. It clears the device's identity entirely —
   * see `session.signOut` for why leaving the old one in place would mean
   * signing nobody out.
   *
   * No navigation afterwards, and that is deliberate: with no identity the
   * shell already resolves every route to the sign-in screen, so pushing a
   * route here would only add a second transition on top of the one the gate
   * is about to run.
   */
  async function signOut(): Promise<void> {
    signingOut = true;
    try {
      await auth.logout();
      await session.signOut();
      toasts.success(t.toasts.signedOut, { key: 'auth:state' });
    } finally {
      signingOut = false;
    }
  }
</script>

<div class="view settings">
  <TopBar title={t.settings.title} />

  <div class="view__body">
    <span class="lbl">{t.settings.audio}</span>

    <PixelSwitch
      label={t.settings.sound}
      checked={soundOn}
      onchange={(next) => {
        settings.setSoundEnabled(next);
      }}
    />

    <PixelSlider
      label={t.settings.music}
      value={settings.audio.music.volume}
      disabled={!soundOn}
      onchange={(next) => {
        settings.setVolume('music', next);
      }}
    />

    <PixelSlider
      label={t.settings.effects}
      value={settings.audio.sfx.volume}
      disabled={!soundOn}
      onchange={(next) => {
        settings.setVolume('sfx', next);
      }}
    />

    <PixelSwitch
      label={t.settings.vibration}
      checked={settings.hapticsEnabled}
      onchange={(next) => {
        settings.setHaptics(next);
      }}
    />

    <span class="lbl settings__group">{t.settings.game}</span>

    <!--
      Live at last. The arrows are `PixelIcon` sprites rather than ◀ ▶ glyphs
      because those two are not in the font subset and would render from a
      fallback face beside pixel type.
    -->
    <div class="opt optrow settings__language">
      <span class="optrow__label">{t.settings.language}</span>
      <span class="settings__language-picker">
        <button
          type="button"
          class="settings__language-arrow"
          aria-label={t.settings.language}
          onclick={() => stepLanguage(-1)}
        >
          <PixelIcon name="back" size={18} />
        </button>
        <span class="settings__language-value">{t.settings.languageName}</span>
        <button
          type="button"
          class="settings__language-arrow"
          aria-label={t.settings.language}
          onclick={() => stepLanguage(1)}
        >
          <PixelIcon name="play" size={18} />
        </button>
      </span>
    </div>

    <PixelSwitch
      label={t.settings.crt}
      checked={settings.display.crt}
      onchange={(next) => {
        settings.setCrt(next);
      }}
    />

    <PixelSwitch
      label={t.settings.reducedMotion}
      hint={t.settings.reducedMotionHint}
      checked={settings.display.reducedMotion}
      onchange={(next) => {
        settings.setReducedMotion(next);
      }}
    />

    <!--
      The commit for everything above it, and nothing below: the account rows
      are actions that take effect on their own. Disabled rather than hidden so
      the screen keeps the same height whether or not anything has changed —
      a button that appears under the player's thumb is a button they press by
      accident.
    -->
    <PixelButton variant="positive" disabled={!settings.dirty} onclick={save}>
      {t.settings.save}
    </PixelButton>

    <span class="lbl settings__group">{t.settings.account}</span>

    <button class="opt optrow settings__account" onclick={() => router.go('profile')}>
      <span class="settings__account-id">
        <PixelAvatar avatarId={session.profile?.avatarId ?? 0} size="sm" />
        <span class="settings__account-name">{session.profile?.displayName ?? t.common.guest}</span>
      </span>
      <span class="settings__account-action">
        {t.settings.edit}
        <PixelIcon name="play" size={18} />
      </span>
    </button>

    <!--
      One slot, two states. Signing in is offered rather than demanded: the app
      has always been playable without an account, and linking one keeps the
      player row that already holds this device's history.
    -->
    {#if auth.signedIn}
      <PixelButton variant="danger" disabled={signingOut} onclick={() => void signOut()}>
        {signingOut ? t.settings.signingOut : t.settings.signOut}
      </PixelButton>
      <span class="tiny settings__note">{t.settings.signedInAs(auth.account?.username ?? '')}</span>
    {:else}
      <PixelButton variant="p1" onclick={() => router.go('login')}>
        {t.settings.signIn}
      </PixelButton>
      <span class="tiny settings__note">{t.settings.progressKept}</span>
    {/if}

    <span class="tiny settings__footer">{t.settings.footer}<br />{t.settings.support}</span>
  </div>

  <!--
    Three ways out, in the order the answer is usually wanted. "Keep editing" is
    last and is also what the backdrop and Escape do, so the safe answer is the
    easy one to give by accident.
  -->
  {#if exitPrompt}
    <PixelDialog
      title={t.settings.unsavedTitle}
      message={t.settings.unsavedMessage}
      oncancel={keepEditing}
    >
      <PixelButton variant="positive" onclick={saveAndExit}>
        {t.settings.saveAndExit}
      </PixelButton>
      <PixelButton variant="danger" onclick={discardAndExit}>
        {t.settings.discardChanges}
      </PixelButton>
      <PixelButton variant="ghost" onclick={keepEditing}>
        {t.settings.keepEditing}
      </PixelButton>
    </PixelDialog>
  {/if}
</div>
